import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { Message, MessagePrev } from "@/types";
import { isMobile } from "react-device-detect";
import { ContentCard } from "../content-card/content-card";
import { useInfoPanelListRowHelpers } from "../../../hooks/useInfoPanelListRowHelpers";

import { selectCompetenceStyles } from "./styles";

interface TagMessagePanelProps {
  tags: string[];
  onMessageClick: (lessonId: string, domain: string, focus: string) => void;
  messageByTag: MessagePrev[];
  isLoadingMessageByTag: boolean;
  errorMessageByTag: null | string;
  fetchMessageByTag: (tag: string) => void;
  lessons?: MessagePrev[];
  messages?: Message[];
  crashCourses?: MessagePrev[];
}

const truncateText = (text: string, maxLength: number = 100): string => {
  if (text?.length <= maxLength) return text;
  return text?.slice(0, maxLength) + "...";
};

export const TagMessagePanel = ({
  tags,
  onMessageClick,
  messageByTag,
  isLoadingMessageByTag,
  errorMessageByTag,
  fetchMessageByTag,
  lessons = [],
  messages = [],
  crashCourses = [],
}: TagMessagePanelProps) => {
  const [selectedCompetence, setSelectedCompetence] = useState<string>("");
  const { getTabbedText, getBadgeLabel } = useInfoPanelListRowHelpers({
    panelRows: messageByTag,
    messages,
    lessons,
    crashCourses,
  });

  const handleCompetenceChange = async (e: SelectChangeEvent<string>) => {
    const newCompetence = e.target.value;
    setSelectedCompetence(newCompetence);
    await fetchMessageByTag(newCompetence);
  };

  const renderLessonsList = (items: MessagePrev[]) => {
    if (!items || items.length === 0) {
      return;
    }

    const maxTextLength = isMobile ? 50 : 100;
    const truncate = (text: string) => truncateText(text, maxTextLength);

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {items.map((item) => {
          const displayItem =
            item.type === "tabbed_message" || item.tabbed_content?.length
              ? { ...item, text: getTabbedText(item) }
              : item;
          const badge = getBadgeLabel(item);
          return (
          <ContentCard
            key={item.message_id}
            item={displayItem}
            onClick={onMessageClick}
            truncateText={truncate}
            variant="tag"
            showBadge
            badgeLabel={badge}
          />
          );
        })}
      </Box>
    );
  };

  const renderLessonsByCompetence = () => {
    if (isLoadingMessageByTag) {
      return <Typography>Loading (by competence)...</Typography>;
    }
    if (errorMessageByTag) {
      return <Typography color="error">{isLoadingMessageByTag}</Typography>;
    }
    return renderLessonsList(messageByTag);
  };

  return (
    <Box>
      <Box sx={{ width: "100%" }} mb={2}>
        <FormControl sx={{ flex: 1, display: "flex" }} size="small">
          <InputLabel
            sx={{ "&.Mui-focused": { color: "#06B6D4" }, display: "flex" }}
            id="competence-select-label"
          >
            Select tag
          </InputLabel>
          <Select
            variant="outlined"
            labelId="tag-select-label"
            label="Select tag"
            value={selectedCompetence}
            onChange={handleCompetenceChange}
            sx={selectCompetenceStyles}
          >
            {tags.map((tag) => (
              <MenuItem key={tag} value={tag}>
                {tag}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {renderLessonsByCompetence()}
    </Box>
  );
};
